import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import { useDateSort } from '../../../hooks/useDateSort';
import apiClient from '../../../utils/axiosClient';
import type { LuckyLotteryRetentionResponse } from '../../../types/luckyLottery.types';

dayjs.locale('ko');

const COLOR_RETENTION = '#111827';
const COLOR_ISSUE = '#2563eb';
const LABEL_ISSUE = '응모권 발급';

// 10%p 단위 구간 색상 (Tailwind blue-50 ~ blue-900)
const RETENTION_COLOR_SCALE = [
  '#EFF6FF', '#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA',
  '#3B82F6', '#2563EB', '#1D4ED8', '#1E40AF', '#1E3A8A',
];

function getRetentionStyle(rate: number): { bg: string; text: string } {
  if (rate <= 0) return { bg: 'transparent', text: COLOR_RETENTION };
  const idx = Math.min(Math.floor(rate / 10), RETENTION_COLOR_SCALE.length - 1);
  return { bg: RETENTION_COLOR_SCALE[idx], text: idx >= 5 ? '#FFFFFF' : COLOR_RETENTION };
}

// ─── 데이터 집계 기준일 (배치는 전일까지만 적재되므로 당일은 데이터가 없음) ──────────────
function getDataCutoffDate(endDate: Dayjs): Dayjs {
  const yesterday = dayjs().subtract(1, 'day');
  return endDate.isBefore(yesterday, 'day') ? endDate : yesterday;
}

// ─── 데이터 변환 ──────────────────────────────────────────────────────────────
type DayStats = { count: number; issueCount: number };
type CohortRow = {
  date: string;
  cohortSize: number;
  entryUserCount: number;
  conversionRate: number;
  days: Record<number, DayStats>;
};

function transformData(items: LuckyLotteryRetentionResponse): { rows: CohortRow[]; maxDay: number } {
  let maxDay = 0;
  const rows: CohortRow[] = items.map((item) => {
    const days: Record<number, DayStats> = {};
    for (const r of item.retention) {
      if (!days[r.retentionDays]) days[r.retentionDays] = { count: 0, issueCount: 0 };
      days[r.retentionDays].count = r.count;
      if (r.retentionDays > maxDay) maxDay = r.retentionDays;
    }
    for (const t of item.ticketRetention) {
      if (!days[t.retentionDays]) days[t.retentionDays] = { count: 0, issueCount: 0 };
      days[t.retentionDays].issueCount = t.count;
      if (t.retentionDays > maxDay) maxDay = t.retentionDays;
    }
    return {
      date: item.date,
      cohortSize: item.cohortSize,
      entryUserCount: item.entryUserCount,
      conversionRate: item.conversionRate,
      days,
    };
  });
  return { rows, maxDay: Math.min(maxDay, 28) };
}

// ─── 코호트 색상 (오래된 코호트: 연함 → 최근 코호트: 진함) ────────────────────────
const COHORT_COLOR_START = { r: 0xbf, g: 0xdb, b: 0xfe }; // #BFDBFE
const COHORT_COLOR_END = { r: 0x1e, g: 0x3a, b: 0x8a };   // #1E3A8A

function getCohortColor(index: number, total: number): string {
  const t = total <= 1 ? 1 : index / (total - 1);
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * t);
  return `rgb(${lerp(COHORT_COLOR_START.r, COHORT_COLOR_END.r)}, ${lerp(COHORT_COLOR_START.g, COHORT_COLOR_END.g)}, ${lerp(COHORT_COLOR_START.b, COHORT_COLOR_END.b)})`;
}

// ─── 코호트별 라인 차트 툴팁 ──────────────────────────────────────────────────
type CohortTooltipPayloadItem = { name: string; value: number | null; color: string };

function CohortTooltip({ active, payload, label }: { active?: boolean; payload?: CohortTooltipPayloadItem[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const items = [...payload]
    .filter((item) => item.value !== null && item.value !== undefined)
    .sort((a, b) => b.name.localeCompare(a.name));
  if (items.length === 0) return null;
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 1.5, p: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', maxHeight: 260, overflowY: 'auto', minWidth: 170 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#1e293b' }}>{label}</Typography>
      {items.map((item) => (
        <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.78rem', color: '#374151' }}>
            {item.name}: <strong>{Number(item.value).toFixed(2)}%</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── 라인 차트 (코호트별 리텐션 커브 오버레이) ───────────────────────────────────
function RetentionLineChart({ rows, maxDay, endDate }: { rows: CohortRow[]; maxDay: number; endDate: Dayjs }) {
  const sortedRows = useMemo(
    () => [...rows].filter((r) => r.cohortSize > 0).sort((a, b) => a.date.localeCompare(b.date)),
    [rows],
  );

  const dataCutoff = useMemo(() => getDataCutoffDate(endDate), [endDate]);

  const chartData = useMemo(() => {
    if (sortedRows.length === 0) return [];
    return Array.from({ length: maxDay + 1 }, (_, i) => i).map((d) => {
      const point: Record<string, string | number | null> = { label: `D${d}` };
      sortedRows.forEach((row) => {
        if (d === 0) {
          point[row.date] = 100;
          return;
        }
        const retentionDate = dayjs(row.date).add(d, 'day');
        if (retentionDate.isAfter(dataCutoff, 'day')) {
          point[row.date] = null;
          return;
        }
        const s = row.days[d] ?? { count: 0, issueCount: 0 };
        point[row.date] = parseFloat(((s.count / row.cohortSize) * 100).toFixed(2));
      });
      return point;
    });
  }, [sortedRows, maxDay, dataCutoff]);

  if (sortedRows.length === 0 || chartData.length === 0) return null;

  const total = sortedRows.length;

  return (
    <Box sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 3, p: 2, mt: 3 }}>
      <Typography variant="h6" align="center" fontWeight="bold" sx={{ mb: 1 }}>
        리텐션 커브
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1.5, fontSize: '0.8rem', color: '#6b7280' }}>
        <span>오래된 코호트</span>
        <Box sx={{ width: 100, height: 8, borderRadius: 1, background: `linear-gradient(to right, rgb(${COHORT_COLOR_START.r}, ${COHORT_COLOR_START.g}, ${COHORT_COLOR_START.b}), rgb(${COHORT_COLOR_END.r}, ${COHORT_COLOR_END.g}, ${COHORT_COLOR_END.b}))` }} />
        <span>최근 코호트</span>
      </Box>
      <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 24, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} interval="preserveStartEnd" />
            <YAxis domain={[0, 'auto']} tickFormatter={(v) => `${v}%`} tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} width={48} />
            <RechartsTooltip content={<CohortTooltip />} />
            {sortedRows.map((row, idx) => (
              <Line
                key={row.date}
                type="monotone"
                dataKey={row.date}
                name={row.date}
                stroke={getCohortColor(idx, total)}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

// ─── 테이블 ───────────────────────────────────────────────────────────────────
function RetentionTable({
  rows, maxDay, selectedApp, startDate, endDate,
}: {
  rows: CohortRow[];
  maxDay: number;
  selectedApp: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
}) {
  const tableRef = useRef<HTMLTableElement>(null);
  const { sorted, sortDirection, toggleSort } = useDateSort(rows, undefined, 'asc');
  const dayColumns = useMemo(() => Array.from({ length: maxDay }, (_, i) => i + 1), [maxDay]);
  const dataCutoff = useMemo(() => getDataCutoffDate(endDate), [endDate]);

  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
    return `럭키복권_리텐션_${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [selectedApp, startDate, endDate]);

  const allRowsData = useMemo(() =>
    sorted.map((row) => [
      row.date,
      row.entryUserCount.toLocaleString('ko-KR'),
      row.cohortSize.toLocaleString('ko-KR'),
      `${row.conversionRate.toFixed(1)}%`,
      ...dayColumns.map((d) => {
        const retentionDate = dayjs(row.date).add(d, 'day');
        if (retentionDate.isAfter(dataCutoff, 'day')) return '—';
        const s = row.days[d] ?? { count: 0, issueCount: 0 };
        const cs = row.cohortSize;
        const r = cs > 0 ? (s.count / cs * 100).toFixed(1) : '0.0';
        const ir = cs > 0 ? (s.issueCount / cs * 100).toFixed(1) : '0.0';
        return `${s.count.toLocaleString('ko-KR')} (${r}%) / ${LABEL_ISSUE} ${s.issueCount.toLocaleString('ko-KR')} (${ir}%)`;
      }),
    ]),
    [sorted, dayColumns, dataCutoff]
  );

  return (
    <Box sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 3, p: 2, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.85rem', color: '#4b5563' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '999px', backgroundColor: COLOR_RETENTION }} />
            <span>리텐션</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '999px', backgroundColor: COLOR_ISSUE }} />
            <span>{LABEL_ISSUE}</span>
          </Box>
        </Box>
        <TableActionsMenu tableRef={tableRef} allRowsData={allRowsData} filename={filename} sheetName="럭키복권 리텐션" />
      </Box>

      <TableContainer sx={{ backgroundColor: '#fff', overflowX: 'auto' }}>
        <Table ref={tableRef} stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <TableSortLabel active direction={sortDirection} onClick={toggleSort}>
                  코호트 날짜
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                진입 유저 수
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                신규 유저 수
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                전환율
              </TableCell>
              {dayColumns.map((d) => (
                <TableCell key={d} align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  D{d}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.date}>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{row.date}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {row.entryUserCount.toLocaleString('ko-KR')}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {row.cohortSize.toLocaleString('ko-KR')}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {row.conversionRate.toFixed(1)}%
                </TableCell>
                {dayColumns.map((d) => {
                  const retentionDate = dayjs(row.date).add(d, 'day');
                  if (retentionDate.isAfter(dataCutoff, 'day')) {
                    return <TableCell key={d} align="right" sx={{ color: '#d1d5db' }}>—</TableCell>;
                  }
                  const s = row.days[d] ?? { count: 0, issueCount: 0 };
                  const retRate = row.cohortSize > 0 ? (s.count / row.cohortSize) * 100 : 0;
                  const issRate = row.cohortSize > 0 ? (s.issueCount / row.cohortSize) * 100 : 0;
                  const cellStyle = getRetentionStyle(retRate);
                  const issueTextColor = cellStyle.text === '#FFFFFF' ? '#BFDBFE' : COLOR_ISSUE;
                  return (
                    <TableCell
                      key={d}
                      align="right"
                      sx={{
                        whiteSpace: 'nowrap',
                        backgroundColor: cellStyle.bg,
                        color: cellStyle.text,
                        transition: 'background-color 0.2s ease-out',
                        py: 0.9,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.3, fontSize: '0.9rem' }}>
                        <Box component="span" sx={{ lineHeight: 1.15 }}>
                          {s.count.toLocaleString('ko-KR')} ({retRate.toFixed(1)}%)
                        </Box>
                        {s.issueCount > 0 && (
                          <Box component="span" sx={{ fontSize: '0.8rem', color: issueTextColor }}>
                            {s.issueCount.toLocaleString('ko-KR')} ({issRate.toFixed(1)}%)
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── 메인 페이지 ──────────────────────────────────────────────────────────────
export default function LuckyLotteryRetention() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [rawData, setRawData] = useState<LuckyLotteryRetentionResponse>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', {
        params: { start_date: startDate.format('YYYY-MM-DD'), end_date: endDate.format('YYYY-MM-DD') },
      })
      .then((res) => setAppOptions(['앱 전체', ...(res.data || [])]))
      .catch(() => undefined);
  }, [startDate, endDate]);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    const app = selectedApp === '앱 전체' ? null : selectedApp;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get<LuckyLotteryRetentionResponse>('/api/v1/analytics/lucky-lottery/retention', {
        params: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
        },
      });
      setRawData(res.data);
    } catch {
      setError('데이터 로드에 실패했습니다.');
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => { fetchAppOptions(); }, [fetchAppOptions]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const { rows, maxDay } = useMemo(() => transformData(rawData), [rawData]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#0044D2' }} />
          <Typography variant="body2" sx={{ ml: 2 }}>데이터 로딩 중...</Typography>
        </Box>
      );
    }
    if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    if (rows.length === 0) {
      return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>데이터가 없습니다.</Typography>;
    }
    return (
      <>
        <RetentionLineChart rows={rows} maxDay={maxDay} endDate={endDate ?? dayjs()} />
        <RetentionTable
          rows={rows}
          maxDay={maxDay}
          selectedApp={selectedApp}
          startDate={startDate ?? dayjs()}
          endDate={endDate ?? dayjs()}
        />
      </>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 1.5, md: 0 } }}>
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Box>
        <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      <Box sx={PAGE_CARD_SX}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        {renderContent()}
      </Box>
    </Box>
  );
}
